import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NotificationService } from '@p2p/shared/domain';
import { catchError, map, of, switchMap, take, tap } from 'rxjs';

import { CreateUserResponse } from '../entities/create-user-response';
import { UserStatus } from '../entities/user-status';
import { UsersDataService } from '../infrastructure/users-data.service';

import * as UsersActions from './users.actions';
import { UsersFacade } from './users.facade';
import { UsersPageParams } from './users.model';

@Injectable()
export class UsersEffects {
    private readonly usersFacade = inject(UsersFacade);
    private readonly actions$ = inject(Actions);
    private readonly usersDataService = inject(UsersDataService);
    private readonly notificationService = inject(NotificationService);
    private readonly translocoService = inject(TranslocoService);

    load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UsersActions.loadUsers),
            switchMap(({ pageParams: newPageParams }) => {
                return this.usersFacade.pageParams$.pipe(
                    map((oldPageParams) => {
                        const pageParams: UsersPageParams = { ...oldPageParams, ...newPageParams };

                        Object.keys(pageParams).forEach((key) => {
                            const paramsKey = key as keyof UsersPageParams;

                            if (pageParams[paramsKey] == undefined || pageParams[paramsKey] === null) {
                                delete pageParams[paramsKey];
                            }
                        });

                        return pageParams;
                    }),
                    take(1)
                );
            }),
            tap((pageParams) => {
                this.usersFacade.setPageParams(pageParams);
            }),
            switchMap((pageParams) =>
                this.usersDataService.getUsers(pageParams).pipe(
                    map((response) => UsersActions.loadUsersSuccess(response)),
                    catchError((error) => of(UsersActions.loadUsersFailure({ error })))
                )
            )
        )
    );

    createUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UsersActions.createUser),
            switchMap(({ user }) => {
                const createUserData = { ...user, email: user.email.toLowerCase() };

                return this.usersDataService.createUser(createUserData).pipe(
                    map(({ registrationToken }: CreateUserResponse) => {
                        this.usersFacade.loadUsers();

                        return UsersActions.createUserSuccess({ registrationToken });
                    }),
                    catchError((error: HttpErrorResponse) => of(UsersActions.createUserFailure({ error })))
                );
            })
        )
    );

    editUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UsersActions.editUser),
            switchMap(({ user }) =>
                this.usersDataService.editUser(user).pipe(
                    map(() => UsersActions.editUserSuccess()),
                    catchError((error: HttpErrorResponse) => of(UsersActions.editUserFailure({ error })))
                )
            )
        )
    );

    deleteUsers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UsersActions.deleteUsers),
            switchMap(({ ids }) =>
                this.usersDataService.deleteUsers(ids).pipe(
                    tap(() =>
                        this.notificationService.open(
                            this.translocoService.translate('users.Users.DeleteUser.Toast.Success')
                        )
                    ),
                    map(() => UsersActions.deleteUsersSuccess({ ids })),
                    catchError((error) => of(UsersActions.deleteUsersFailure({ ids, error })))
                )
            )
        )
    );

    changeUserBlocked$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UsersActions.changeUserBlocked),
            switchMap(({ id, blocked }) =>
                this.usersDataService.changeUserBlocked(id, blocked).pipe(
                    map(() => {
                        const status = blocked ? UserStatus.ACTIVE : UserStatus.BLOCKED;

                        return UsersActions.changeUserBlockedSuccess({ id, changes: { status } });
                    }),
                    catchError((error) => of(UsersActions.changeUserBlockedFailure({ id, error })))
                )
            )
        )
    );
}
